import { IRecord } from "../types/schema"
import { convertPriceCurrency, isCurrencyMatch } from "../utils/currency"
import { IBranchSettings } from "../utils/useSettings"

export function useProductGroupProductPrice() {
  return (productId: string) => getPrice(productId)
}

export type IProductGroupPriceResponse = IRecord<
  "products",
  "cost_price_pennies" | "body" | "line_item_description" | "preferred_supplier_list_price_pennies"
> & {
  branch?: IRecord<"branches", "name" | "currency_name" | "euro_pound_exchange_rate">
  preferred_supplier?: IRecord<"suppliers", "name" | "currency_name">
}

async function getPrice(productId: string) {
  // Look up our product, get its master, and master group (only if group is useMasterPrice & has primary branch)
  const product = await window.skeem.fetch2<
    IRecord<"products", "stock_code"> & {
      branch?: { settings?: IBranchSettings }
      product_group_master?: { primary_branch: { id: string } }
      product_master?: IRecord<"product_masters", "stock_code">
    }
  >({
    first: {
      name: "products",
      attributes: [
        "stock_code",
        { name: "product_master", attributes: ["stock_code"] },
        { name: "branch", attributes: ["settings"] },
        {
          as: "product_group_master",
          value: {
            path: [
              "product_group",
              {
                name: "product_group_master",
                attributes: ["primary_branch"],
                filter: {
                  and: [
                    { matches: { useMasterPrice: true } },
                    { exists: { attr: "primary_branch" } },
                  ],
                },
              },
            ],
          },
        },
      ],
      filter: { matches: { id: productId } },
    },
  })

  if (
    !product ||
    product.stock_code !== product.product_master?.stock_code ||
    !product.product_group_master?.primary_branch ||
    !product.product_master ||
    product.branch?.settings?.excludeFromGroupedPricing
  ) {
    return null
  }
  // Lookup the products on the returned master, filter them to only include primary branch products,
  // return the first.
  const pmWithProduct = await window.skeem.fetch2<
    IRecord<"product_masters"> & {
      products: IProductGroupPriceResponse[]
    }
  >({
    first: {
      name: "product_masters",
      attributes: [
        {
          name: "products",
          attributes: [
            "cost_price_pennies",
            "preferred_supplier_list_price_pennies",
            "body",
            "line_item_description",
            { name: "preferred_supplier", attributes: ["name", "currency_name"] },
            {
              name: "branch",
              attributes: ["name", "currency_name", "euro_pound_exchange_rate"],
            },
          ],
          filter: {
            matches: { branch: product.product_group_master?.primary_branch?.id },
          },
        },
      ],
      filter: { matches: { id: product.product_master.id } },
    },
  })

  return pmWithProduct.products[0]
    ? { ...pmWithProduct.products[0], original_product_id: productId }
    : null
}

type ISupplier = IRecord<"suppliers", "currency_name">

export function groupPriceConverted(
  product: IRecord<"products", "cost_price_pennies"> & {
    product_group: IRecord<"product_groups", "products_price_method">
    preferred_supplier?: ISupplier
    suppliers: ISupplier[]
  },
  groupPrice: IProductGroupPriceResponse | null | undefined,
  currentUser?:
    | (IRecord<"skeem_users"> & {
        branch: IRecord<"branches", "euro_pound_exchange_rate" | "currency_name">
      })
    | null,
  currencyName?: string,
) {
  if (!product || !groupPrice || !currentUser) {
    return null
  }
  // GROUP PRICING
  // set the supplier to the group price supplier
  const supplier =
    groupPrice?.preferred_supplier ?? product.preferred_supplier ?? product.suppliers[0]

  // set the etp to the group product's branch exchange rate
  const euroToPound =
    groupPrice?.branch?.euro_pound_exchange_rate ?? currentUser.branch.euro_pound_exchange_rate ?? 1

  // setup a base price, so that don't have to do this check everywhere product.cost_price_pennies ?? 0 was done
  const basePrice = groupPrice?.cost_price_pennies ?? product.cost_price_pennies ?? 0

  if (!supplier) {
    console.warn(`Skipped currency check: no supplier for product - id: ${product.id}`)
    return basePrice
  }

  if (!supplier.currency_name || !(currencyName || currentUser.branch.currency_name)) {
    console.error(
      `Skipped currency check: Missing currency names, supplier is ${
        supplier.currency_name
      }, target is ${currencyName || currentUser.branch.currency_name}`,
    )
    return basePrice
  }

  if (!basePrice) {
    return 0
  }

  if (
    isCurrencyMatch(supplier.currency_name, currencyName || currentUser.branch.currency_name) ||
    product.product_group?.products_price_method === "average_buying_price_pennies"
  ) {
    return basePrice
  }

  return convertPriceCurrency(
    basePrice,
    euroToPound,
    supplier.currency_name,
    currencyName || currentUser.branch.currency_name,
  )
}

export type IGroupPrice = Awaited<ReturnType<typeof getPrice>>
